import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { MAX_BODY_LENGTH } from '@sokdaq/protocol';
import type { MapObject } from '@sokdaq/room-core';
import { ConnectionBanner } from '../components/ConnectionBanner.js';
import { Joystick } from '../components/Joystick.js';
import { useIsTouch } from '../hooks/useTouch.js';
import { createScene, type SceneHandle } from '../office/scene.js';
import { useChatStore } from '../store/chat.js';

/**
 * 픽셀 사무실.
 *
 * 캔버스는 React 밖에서 돌고(scene.ts), 여기서는 입력 포커스와 안내
 * 오버레이만 맡는다. 렌더 루프를 컴포넌트에 올리면 초당 60번 리렌더가 된다.
 */
export function OfficeView() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<SceneHandle | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [nearby, setNearby] = useState<MapObject | null>(null);
  const [chatting, setChatting] = useState(false);
  const [whisperMode, setWhisperMode] = useState(true);
  const [draft, setDraft] = useState('');

  const status = useChatStore((s) => s.status);
  const sendText = useChatStore((s) => s.sendText);
  const sendWhisper = useChatStore((s) => s.sendWhisper);
  const isTouch = useIsTouch();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;

    const scene = createScene({ canvas, onNearbyObjectChange: setNearby });
    sceneRef.current = scene;
    return () => {
      scene.destroy();
      sceneRef.current = null;
    };
  }, []);

  // 채팅 중에는 WASD가 글자로 가야 한다.
  useEffect(() => {
    sceneRef.current?.setInputEnabled(!chatting);
  }, [chatting]);

  useEffect(() => {
    sceneRef.current?.setWhisperMode(whisperMode);
  }, [whisperMode]);

  // 걸어다니는 상태에서의 단축키. 채팅 중에는 입력창이 가져간다.
  useEffect(() => {
    if (chatting) return;

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      /*
       * 입력창에서 올라온 이벤트는 건드리지 않는다.
       *
       * React는 루트 컨테이너에 리스너를 달기 때문에 입력창의 onKeyDown이
       * 먼저 돌고, 그 안에서 blur()를 부르면 chatting=false가 곧바로 반영돼
       * 이 리스너가 되살아난다. 그 상태로 같은 Enter가 window까지 올라오면
       * 방금 떠난 포커스를 다시 가져가서, 보내고 나서도 입력창에 갇힌다.
       * 이벤트 순서에 기대지 않고 출처로 걸러내는 편이 안전하다.
       */
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        /*
         * 포커스는 여기서 곧바로 옮긴다.
         *
         * setChatting만 하고 다음 프레임에 focus()를 부르면, 그 사이에
         * 입력한 글자가 캔버스로 흘러가 사라진다. Enter를 누르자마자
         * 바로 타이핑하는 게 자연스러운 동작이라 실제로 자주 걸린다.
         * 입력창은 항상 DOM에 있으므로 지금 포커스를 줄 수 있고,
         * chatting은 onFocus가 올려준다.
         */
        inputRef.current?.focus();
        return;
      }
      if (event.code === 'KeyE') {
        event.preventDefault();
        sceneRef.current?.interactNearby();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [chatting]);

  function submit() {
    const body = draft.trim();
    if (body !== '' && status === 'open') {
      if (whisperMode) sendWhisper(body);
      else sendText(body);
    }
    setDraft('');
    // blur가 chatting을 내려주고, 그 순간부터 다시 걸을 수 있다.
    inputRef.current?.blur();
  }

  function onDraftKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      // 입력기가 조합을 확정하는 Enter는 전송이 아니다. 한글을 치다가
      // 마지막 글자를 확정하는 순간 문장이 날아가면 안 된다.
      if (event.nativeEvent.isComposing) return;
      event.preventDefault();
      submit();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setDraft('');
      setChatting(false);
    }
  }

  return (
    <div className="relative flex h-full flex-col">
      <ConnectionBanner />

      <div className="relative min-h-0 flex-1">
        <canvas
          ref={canvasRef}
          tabIndex={-1}
          // 터치에서 캔버스 위 드래그는 조이스틱 몫이다. 페이지가 함께
          // 움직이거나 더블탭 확대가 걸리면 조작이 되지 않는다.
          className="no-touch-scroll block h-full w-full outline-none"
        />

        {/* 오브젝트 옆에 섰을 때만. 늘 떠 있으면 화면을 가린다. */}
        {nearby !== null && !chatting && !isTouch && (
          <div className="pointer-events-none absolute left-1/2 top-4 -translate-x-1/2 rounded bg-black/70 px-3 py-1.5 text-xs text-neutral-200">
            <kbd className="rounded bg-neutral-700 px-1">E</kbd> {nearby.label}
          </div>
        )}

        {isTouch ? (
          !chatting && (
            <>
              <Joystick onChange={(dx, dy) => sceneRef.current?.setStickVector(dx, dy)} />

              {/*
                상호작용 버튼은 닿는 것이 있을 때만 나타난다. 늘 띄워 두면
                눌러도 아무 일이 없어서 고장난 것처럼 보인다.
              */}
              {nearby !== null && (
                <button
                  type="button"
                  onClick={() => sceneRef.current?.interactNearby()}
                  className="absolute bottom-8 right-5 h-16 w-16 rounded-full border border-neutral-600/60 bg-black/50 text-[11px] leading-tight text-neutral-200"
                >
                  {nearby.label}
                </button>
              )}
            </>
          )
        ) : (
          <div className="pointer-events-none absolute bottom-3 left-3 rounded bg-black/60 px-2.5 py-1.5 text-[10px] leading-relaxed text-neutral-400">
            <div>
              <kbd className="text-neutral-200">WASD</kbd> 이동 ·{' '}
              <kbd className="text-neutral-200">Enter</kbd> 말하기 ·{' '}
              <kbd className="text-neutral-200">E</kbd> 상호작용
            </div>
          </div>
        )}
      </div>

      {/*
        입력창은 늘 DOM에 둔다. 조건부로 마운트하면 Enter를 누른 프레임에
        focus()를 걸 대상이 없어서, 바로 이어 친 글자가 사라진다.
      */}
      <div className="flex items-center gap-2 border-t border-neutral-800 bg-neutral-950 p-3">
        <button
          type="button"
          onClick={() => setWhisperMode((on) => !on)}
          aria-pressed={whisperMode}
          title="속닥은 가까운 사람에게만 들리고 기록에 남지 않습니다"
          className={`shrink-0 rounded px-2 py-1 text-xs ${
            whisperMode ? 'bg-violet-600 text-white' : 'bg-neutral-800 text-neutral-400'
          }`}
        >
          속닥
        </button>
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onDraftKeyDown}
          onFocus={() => setChatting(true)}
          onBlur={() => setChatting(false)}
          // 모바일 키보드의 리턴 키를 '전송'으로 바꾼다. 한 줄 입력이라
          // 줄바꿈이 필요 없으므로 Enter 전송이 그대로 맞다.
          enterKeyHint="send"
          // placeholder는 상태에 따라 바뀐다. 이름은 고정으로 따로 준다 —
          // 스크린리더가 매번 다른 이름을 읽으면 같은 칸인지 알 수 없다.
          aria-label="말하기"
          maxLength={MAX_BODY_LENGTH}
          placeholder={
            chatting
              ? whisperMode
                ? '가까운 사람에게만…'
                : '모두에게…'
              : // 터치에는 Enter가 없다. 안내를 그대로 두면 아무도 못 찾는다.
                isTouch
                ? whisperMode
                  ? '탭해서 속닥…'
                  : '탭해서 말하기…'
                : whisperMode
                  ? '속닥이려면 Enter'
                  : '말하려면 Enter'
          }
          className={`min-w-0 flex-1 rounded-lg border bg-neutral-900 px-3 py-2 text-sm outline-none placeholder:text-neutral-600 ${
            chatting ? 'border-neutral-600' : 'border-neutral-800'
          }`}
        />
        {chatting && isTouch ? (
          // 터치에서는 Esc가 없다. 나갈 문을 손으로 누를 수 있어야 한다.
          <button
            type="button"
            /*
             * 포커스가 옮겨가지 않게 막는다.
             *
             * 그냥 두면 누르는 순간 입력창이 blur되고 chatting이 false가 돼서
             * 이 버튼이 사라진다 — click이 도착할 대상이 없어져 아무 일도
             * 일어나지 않는다. 자기 자신을 없애는 버튼이 되는 셈이다.
             */
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => {
              setDraft('');
              inputRef.current?.blur();
            }}
            className="tap shrink-0 rounded px-2 text-xs text-neutral-400"
          >
            취소
          </button>
        ) : (
          <span className="shrink-0 text-[10px] text-neutral-600">{chatting ? 'Esc 취소' : ''}</span>
        )}
      </div>
    </div>
  );
}
